import { useMemo } from 'react'

import {
  CostMessagingCoverageModalData,
  CostMessagingModalContent,
  CoverageType,
  GetCostMessagingModalTextParams,
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic'

interface UseGetCostMessagingInfoProps {
  isCostMessagingEnabled: boolean
  costMessagingModalText?: {
    [key in CoverageType]: (params: GetCostMessagingModalTextParams) => CostMessagingModalContent[]
  }
  coverageModalTitleText?: React.ReactNode
  isHPIExclusive?: boolean
  isEAPExclusive?: boolean
  isDependentDisabled?: boolean
  isCustomerEAPMeds?: boolean
  isCustomerHPIMeds?: boolean
  isCustomerProgramEAPCoaching?: boolean
  isCustomerProgramHPITherapy?: boolean
  isCustomerProgramEAPTherapy?: boolean
  customerDisplayName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
}
/**
 * Hook to retrieve cost messaging information related to a customer's provided coverage
 */
export const useGetCostMessagingInfo = ({
  isCostMessagingEnabled,
  costMessagingModalText,
  coverageModalTitleText,
  isHPIExclusive,
  isEAPExclusive,
  isDependentDisabled,
  isCustomerEAPMeds,
  isCustomerHPIMeds,
  isCustomerProgramEAPCoaching,
  isCustomerProgramHPITherapy,
  isCustomerProgramEAPTherapy,
  customerDisplayName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
}: UseGetCostMessagingInfoProps): CostMessagingCoverageModalData => {
  return useMemo(() => {
    if (isCostMessagingEnabled) {
      const customerCoverageType = isHPIExclusive
        ? CoverageType.HPI_ONLY
        : isEAPExclusive
        ? CoverageType.EAP_ONLY
        : CoverageType.EAP_AND_HPI

      let coverageModalContents: CostMessagingModalContent[] = []

      if (costMessagingModalText) {
        coverageModalContents = costMessagingModalText[customerCoverageType]({
          isDependentDisabled,
          isCustomerEAPMeds,
          isCustomerHPIMeds,
          isCustomerProgramEAPCoaching,
          isCustomerProgramHPITherapy,
          isCustomerProgramEAPTherapy,
        })
      }

      console.debug(
        `Cost messaging modal - is supported configuration (coverageModalContents.length > 0): ${
          coverageModalContents.length > 0
        },
        [customerCoverageType: ${customerCoverageType}],
        [isDependentDisabled: ${isDependentDisabled}],
        [isCustomerEAPMeds: ${isCustomerEAPMeds}],
        [isCustomerHPIMeds: ${isCustomerHPIMeds}],
        [isCustomerProgramEAPCoaching: ${isCustomerProgramEAPCoaching}],
        [isCustomerProgramHPITherapy: ${isCustomerProgramHPITherapy}],
        [isCustomerProgramEAPTherapy: ${isCustomerProgramEAPTherapy}],
        `,
      )

      return {
        coverageModalContents,
        coverageModalTitleText,
        isCostMessagingEnabled,
        costMessagingModalCoverageInfo: { customerCoverageType },
        customerDisplayName,
        numSessions,
        numberOfSessionByProgram,
        displayVisitsPerIssuePerYear,
        displayVisitsPerIssuePerYearByProgram,
      }
    }

    return {
      coverageModalContents: [],
      coverageModalTitleText,
      isCostMessagingEnabled,
      costMessagingModalCoverageInfo: undefined,
      customerDisplayName,
      numSessions,
      numberOfSessionByProgram,
      displayVisitsPerIssuePerYear,
      displayVisitsPerIssuePerYearByProgram,
      isDependentDisabled,
    }
  }, [
    isCostMessagingEnabled,
    coverageModalTitleText,
    customerDisplayName,
    numSessions,
    numberOfSessionByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
    isDependentDisabled,
    isHPIExclusive,
    isEAPExclusive,
    costMessagingModalText,
    isCustomerEAPMeds,
    isCustomerHPIMeds,
    isCustomerProgramEAPCoaching,
    isCustomerProgramHPITherapy,
    isCustomerProgramEAPTherapy,
  ])
}
